// ** React Imports
import { useContext, useEffect, useMemo, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";

// ** Redux & Store & Actions
import { useDispatch, useSelector } from "react-redux";
import {
	setFakeUserData,
	setSentSmsCodeTime,
	setPhoneNumber as setPhoneNumerStore,
} from "store/modules/auth";

// ** Third Party Components
import { useTranslation } from "react-i18next";

// ** Custom Components
import Block from "components/layout/Block";
import Text from "components/typography/Text";
import SelectorSelectSingle from "components/layout/DropdownSelectors/SelectorSelectSingle";
import InputSelector from "components/layout/InputSelector";
import ErrorMessage from "containers/Landing/components/ErrorMessage";
import Tooltip from "components/layout/Tooltip";
import { ButtonLanding } from "components/layout/Button/ButtonLanding";
import StepLabel from "../Register/components/StepLabel";

// ** Hooks
import { useSendSmsCode } from "./hooks/useSendSmsCode";
import { useBlockResendSms } from "./hooks/useBlockResendSms";

// ** Utils & Helpers
import api from "helpers/ApiClient";

// ** Contexts
import deviceTypeContext from "contexts/deviceTypeContext";

// ** Data
import { countryCodes } from "resources/data/countries-codes";

// #####################################################

const RegisterVerifyPhone = () => {
	const { isPhone } = useContext(deviceTypeContext);

	const { t } = useTranslation(["common", "landing"]);

	const { login, id, hash } = useSelector(
		(state) => state.globalCommon.registrationVerifyPhoneModal
	);

	const dispatch = useDispatch();

	const { state } = useLocation();
	const history = useHistory();

	const [countryCode, setCountryCode] = useState(
		state?.countryCode ? Number(state?.countryCode) : 48
	);
	const [countryCodeError, setCountryCodeError] = useState(false);

	const [isoCountryCode, setIsoCountryCode] = useState(
		state?.isoCountryCode || "pl"
	);
	const [phoneNumber, setPhoneNumber] = useState(state?.phoneNumber || "");
	const [phoneNumberError, setPhoneNumberError] = useState(false);

	const fakeUserLogin = useSelector((state) => state.auth.fakeUserData.login);

	const { sendSmsCode, isSending } = useSendSmsCode();
	const {
		isResendDisabledByTime,
		remainingTimeString,
		remainingTimeNumber,
		setBlockResendSms,
	} = useBlockResendSms();

	const countryItemFinal = useMemo(() => {
		const mappedCountries = countryCodes.map((item) => {
			return {
				...item,
				key: `${item.phone_code_as_number}:${item.iso_code}`,
				value: item.phone_code_as_number,
				content: `+${item.phone_code}`,
				searchContent: `${t(`countries:${item.iso_code}`)} +${
					item.phone_code
				}`,
				justifyEndContent: t(`countries:${item.iso_code}`),
			};
		});

		// Sortowanie - aktualnie wybrany kraj na początku
		return mappedCountries.sort((a, b) => {
			if (a.value === countryCode) return -1;
			if (b.value === countryCode) return 1;
			return 0;
		});
	}, [countryCode, t]);

	useEffect(() => {
		api.post(`/authorization/registration/check`, {
			id: +id,
			hash,
			login,
		})
			.then((res) => {
				dispatch(
					setFakeUserData({
						...res.data,
						cityName: res.data.city,
						regionName: res.data.region,
					})
				);
				dispatch(
					setSentSmsCodeTime({
						sentTime: res.data?.sms?.send,
						blockTime: res.data?.sms?.block,
					})
				);
				dispatch(
					setPhoneNumerStore({
						phoneNumber: res.data?.sms?.number,
						countryCode: res.data?.sms?.country,
						isoCountryCode: res.data?.sms?.countryISO,
					})
				);

				if (
					res.data?.sms?.number &&
					res.data?.sms?.country &&
					!res.data?.sms?.allow_change
				) {
					history.push(`/registration-code/${login}/${id}/${hash}`);
				}
			})
			.catch((err) => {
				if (err?.response?.status === 403) {
					history.push("/");
				}
			});
	}, []);

	// #####################################################

	return (
		<Block flex column fullWidth>
			<StepLabel label={t("landing:register.otp.step1.title")} />
			<Block flex column fullWidth withRowGap>
				<Block flex justifyCenter mb={4}>
					<Text textCenter>
						{t("landing:register.otp.step1.infoContent")}
					</Text>
				</Block>
				<Block flex column fullWidth>
					<Block flex>
						<SelectorSelectSingle
							emptyText={t("filtersAndSelectors:countryLabel")}
							label={t("filtersAndSelectors:countryLabel")}
							items={countryItemFinal}
							selected={countryCode}
							onAccept={(item) => {
								if (item === null) {
									setCountryCode(null);
									setIsoCountryCode(null);
									setCountryCodeError(true);
									return;
								}

								setCountryCode(item.value);
								setCountryCodeError(false);

								const isoCountryCodeValue = countryCodes.find(
									(countryItem) =>
										countryItem.phone_code_as_number ===
										item.value
								);
								setIsoCountryCode(isoCountryCodeValue.iso_code);

								if (
									`${item.value}` === state?.countryCode &&
									remainingTimeNumber
								) {
									setBlockResendSms(true);
								} else {
									setBlockResendSms(false);
								}
							}}
							activatorStyle={{
								borderTopRightRadius: "0px",
								borderBottomRightRadius: "0px",
								borderRightColor: "var(--dz-sys-divider-color)",
								color: "var(--dz-sys-input-text-color)",
							}}
							activatorWrapperStyle={{
								marginRight: "0px",
								minWidth: "100px",
								maxWidth: "none",
								borderTopRightRadius: "0px",
								borderBottomRightRadius: "0px",
							}}
							activatorStyleOptions={{
								noPad: true,
								noFullWidth: true,
								withLabel: false,
								isLanding: true,
								hideLabel: true,
							}}
							withMobileVersion
							customLayerStyle={{
								overflowY: "auto",
								zIndex: 10000,
								color: "var(--dz-sys-input-text-color)",
							}}
							useLayerOptions={{
								overflowContainer: !isPhone,
							}}
							withFullItem
							withSearchInput
							closeWithoutEnableScroll
							customInputWidth={"230px"}
							customWidthWhenDropdownOpen={230}
							isLanding
						/>

						<Block fullWidth>
							<InputSelector
								value={phoneNumber}
								label={t(
									"landing:register.otp.step1.inputPlaceholder"
								)}
								placeholder={t(
									"landing:register.otp.step1.inputPlaceholder"
								)}
								flex1
								noPad
								noMinWidth
								maxlength="14"
								size="14"
								additionalOnChangeAction={(value) => {
									let actualValue = value.replace(/\D/g, "");
									setPhoneNumber(actualValue);
									if (
										actualValue === state?.phoneNumber &&
										remainingTimeNumber
									) {
										setBlockResendSms(true);
									} else {
										setBlockResendSms(false);
									}
								}}
								onAccept={() => {
									const fullPhoneNumber = `${countryCode}${phoneNumber}`;
									if (fullPhoneNumber.length > 15) {
										setPhoneNumberError(true);
									}
								}}
								onFocus={() => {
									setPhoneNumberError(false);
								}}
								selectorWrapperStyle={{
									borderTopLeftRadius: "0px",
									borderBottomLeftRadius: "0px",
								}}
								selectorSectionStyle={{
									borderTopLeftRadius: "0px",
									borderBottomLeftRadius: "0px",
								}}
								withLabel={false}
								customColor="var(--dz-sys-input-text-color)"
							/>
						</Block>
					</Block>
					{phoneNumberError && (
						<ErrorMessage>
							{t("landing:register.otp.step1.invalidPhoneNumber")}
						</ErrorMessage>
					)}
					{countryCodeError && (
						<ErrorMessage>
							{t("landing:register.otp.step1.invalidCountryCode")}
						</ErrorMessage>
					)}
				</Block>
				<Tooltip
					text={
						isResendDisabledByTime &&
						`${t(
							"landing:register.otp.step2.resendTooltipText"
						)} ${remainingTimeString}`
					}
					wrapperStyle={{
						position: "relative",
						left: 0,
						right: 0,
						top: 0,
						bottom: 0,
					}}
					triggerOffset={6}
				>
					<ButtonLanding
						block
						label={t("landing:register.otp.step2.buttonText")}
						onClick={() => {
							sendSmsCode({
								phoneNumber,
								countryCode,
								isoCountryCode,
							});
						}}
						disabled={isSending}
					>
						{t("landing:register.otp.step2.buttonText")}
					</ButtonLanding>
				</Tooltip>
			</Block>
		</Block>
	);
};

// #####################################################

export default RegisterVerifyPhone;

// #####################################################
